import { Lock } from 'lucide-react';
import { ALL_PLAY_MODES, playModeConfig, type PlayMode } from '../../services/playModes';

export const PLAY_MODE_LOCK_NOTE =
  "The player is locked because this event has requests. Requests store song IDs for this player, so it can't change now.";

interface PlayModePickerProps {
  value: PlayMode | null;
  onChange: (mode: PlayMode) => void;
  locked: boolean;
  lockNote?: string;
  name?: string;
}

export function PlayModePicker({ value, onChange, locked, lockNote = PLAY_MODE_LOCK_NOTE, name = 'playMode' }: PlayModePickerProps) {
  const selectable = ALL_PLAY_MODES.map(playModeConfig).filter(mode => mode.selectable);
  const comingLater = ALL_PLAY_MODES.map(playModeConfig).filter(mode => !mode.selectable);

  return (
    <div className="space-y-3">
      <div className="space-y-2" role="radiogroup" aria-label="Player">
        {selectable.map(mode => {
          const checked = value === mode.id;
          return (
            <label
              key={mode.id}
              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                checked
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 dark:border-gray-700'
              } ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary/50'}`}
            >
              <input
                type="radio"
                name={name}
                value={mode.id}
                checked={checked}
                disabled={locked}
                onChange={() => onChange(mode.id)}
                className="mt-1 accent-primary"
              />
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{mode.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{mode.description}</p>
              </div>
            </label>
          );
        })}
        {comingLater.map(mode => (
          <div
            key={mode.id}
            className="flex items-start gap-3 p-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 opacity-50"
          >
            <input type="radio" disabled className="mt-1" aria-label={mode.name} />
            <div>
              <p className="font-medium text-gray-900 dark:text-white text-sm">
                {mode.name} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">· Coming later</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{mode.description}</p>
            </div>
          </div>
        ))}
      </div>
      {locked && (
        <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
          <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {lockNote}
        </p>
      )}
    </div>
  );
}
